
import { useState, useMemo } from 'react';
import { LeadsHeader } from './leads/LeadsHeader';
import { LeadsTable } from './leads/LeadsTable';
import { EmptyState } from './leads/EmptyState';
import { Card } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { EnhancedLead } from '@/hooks/use-dashboard-data';
import { Button } from '@/components/ui/button';
import { PlusCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface NursingHomeLeadsProps {
  leads: EnhancedLead[];
  onLeadUpdate: () => void;
}

export function NursingHomeLeads({ leads, onLeadUpdate }: NursingHomeLeadsProps) {
  const [filters, setFilters] = useState({
    status: null as string | null,
    county: null as string | null,
  });
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  console.log('NursingHomeLeads component received leads:', leads);

  // Extract unique counties from leads for filtering
  const counties = useMemo(() => {
    const uniqueCounties = new Set<string>();
    leads.forEach(lead => {
      if (lead.location) {
        uniqueCounties.add(lead.location);
      }
    });
    return Array.from(uniqueCounties);
  }, [leads]);

  const handleFilterChange = (filterType: 'status' | 'county', value: string | null) => {
    // Handle the "all" special value to clear the filter
    if (value === 'all') {
      value = null;
    }
    
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleExploreNursingHomes = () => {
    navigate('/nursing-homes');
  };

  const handleAddToWaitlist = () => {
    navigate('/waitlist');
  };

  return (
    <Card className={isMobile ? "p-3" : "p-6"}>
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-grandpal-green-dark mb-2">Your Nursing Home Leads</h2>
        <p className="text-gray-600">
          Track your nursing home applications and get updates on availability
        </p>
      </div>
      
      {leads && leads.length > 0 ? (
        <>
          <LeadsHeader 
            totalLeads={leads.length} 
            counties={counties} 
            onFilterChange={handleFilterChange} 
            filters={filters}
          />
          <div className={isMobile ? "mt-3" : "mt-5"}>
            <LeadsTable leads={leads} onLeadUpdate={onLeadUpdate} filters={filters} />
          </div>
          
          <div className="mt-6 p-4 bg-grandpal-green-lighter/20 border border-grandpal-green/20 rounded-lg">
            <h3 className="font-semibold text-grandpal-green-dark mb-2">Next Steps</h3>
            <p className="text-sm text-gray-600 mb-3">
              We've notified these nursing homes about your interest. They'll contact you directly if they have availability.
            </p>
            <Button 
              className="bg-grandpal-green hover:bg-grandpal-green/90 flex items-center gap-2"
              onClick={handleAddToWaitlist}
              size="sm"
            >
              <PlusCircle className="h-4 w-4" />
              Add More Nursing Homes
            </Button>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <EmptyState />
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <Button 
              className="bg-grandpal-green hover:bg-grandpal-green/90 flex items-center gap-2"
              onClick={handleExploreNursingHomes}
            >
              <PlusCircle className="h-4 w-4" />
              Explore Nursing Homes
            </Button>
            <Button 
              className="bg-grandpal-green-dark hover:bg-grandpal-green-dark/90 flex items-center gap-2"
              onClick={handleAddToWaitlist}
            >
              Join Waitlist
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
